import * as React from 'react'
import styled from '@emotion/styled'
import { CloseIcon } from '../icons'
import { Button, ButtonType } from '../button/button'
import { Secondary, mixins } from '@sphinx/components-styles'
import { NotificationAnimation, getAnimation } from './animation'
import { ActionBar, LeftActions, RightActions } from '../actionBar/action-bar'

export enum NotificationType {
  regular = 'regular',
  success = 'success',
  error = 'error',
}

export interface NotificationProps
  extends React.HTMLAttributes<HTMLDivElement> {
  message: string | JSX.Element
  type?: NotificationType
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
  durationMs?: number
  animation?: NotificationAnimation
  onClose(): void
}

const NotificationMessage = styled.span`
  ${mixins.body}
  line-height: 16px;
`

const Panel = styled(ActionBar, {
  shouldForwardProp: (prop) => !['type', 'animation'].includes(prop),
})<{
  type: NotificationType
  animation: NotificationAnimation
}>`
  padding: 1.5px 24px;
  background-color: ${({ type }) => {
    if (type === NotificationType.regular) {
      return Secondary.yellow[20]
    }

    return type === NotificationType.success
      ? Secondary.green[20]
      : Secondary.red[20]
  }};

  box-shadow: 0px 1px 0px rgba(0, 0, 0, 0);

  ${({ animation }) => animation && getAnimation(animation)}
`

// Notification bar should be closed at the same time
// or before the animation is finished in order to prevent component flickering.
// Sometimes animation is completing faster then
// we're automatically closing the notification bar.
// This time gap is fixing this problem.
const leaveAnimationTimeGapMs = 100

export const Notification: React.FC<NotificationProps> = ({
  icon: NotificationIcon,
  message,
  onClose,
  children: actions,
  durationMs,
  animation,
  type = NotificationType.regular,
  ...rest
}) => {
  const animationValue = React.useMemo(() => {
    return animation && animation.leave
      ? {
          ...animation,
          leave: {
            ...animation.leave,
            delayMs: durationMs,
          },
        }
      : animation
  }, [animation, durationMs])

  React.useEffect(() => {
    if (durationMs && durationMs > 0) {
      const delta =
        animation && animation.leave
          ? animation.leave.durationMs - leaveAnimationTimeGapMs
          : 0

      const timerId = setTimeout(onClose, durationMs + delta)

      return () => {
        if (timerId) {
          clearTimeout(timerId)
        }
      }
    }
  }, [])

  return (
    <Panel type={type} animation={animationValue} {...rest}>
      <LeftActions>
        {NotificationIcon && (
          <NotificationIcon style={{ paddingRight: '16px' }} />
        )}
        <NotificationMessage>{message}</NotificationMessage>
      </LeftActions>
      <RightActions>
        {actions}
        <Button buttonType={ButtonType.GHOST} onClick={onClose}>
          <CloseIcon />
        </Button>
      </RightActions>
    </Panel>
  )
}
